import React from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useFormikContext } from 'formik'
import { Box, Typography, Collapse, Alert, InputAdornment } from '@mui/material'
import { useGetManageableWaitingRoomsQuery } from '@/core/services/waiting-room'
import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import SwitchField from '@/components/formik/fields/SwitchField'
import TextField from '@/components/formik/fields/TextField'

export type PaymentReceiverStaff = {
  _id: string
  userInfo?: {
    name?: string
    surname?: string
  }
  stripeActivated?: boolean
  stripe?: string
}

export type ImmediateConsultationConfigFormProps = {
  readOnly?: boolean
  staffs?: PaymentReceiverStaff[]
}

export function ImmediateConsultationConfigForm(props: ImmediateConsultationConfigFormProps) {
  const { readOnly = false, staffs = [] } = props
  useLingui()
  const { values } = useFormikContext<any>()
  const { data: waitingRooms, isLoading: loadingWaitingRooms } = useGetManageableWaitingRoomsQuery()

  const isEnabled = values.immediateConsultationConfig?.enabled
  const requiresPayment = values.immediateConsultationConfig?.requirePaymentUpfront
  const hasNoWaitingRooms = !loadingWaitingRooms && waitingRooms?.length === 0

  const waitingRoomOptions = (waitingRooms || []).map((wr) => ({
    label: wr.network ? `${wr.name} (${wr.network.name})` : wr.name,
    value: wr._id,
  }))

  // Show all staffs from the meeting link, indicate which ones have Stripe activated
  const paymentReceiverOptions = staffs.map((staff) => ({
    label: `${staff.userInfo?.name ?? ''} ${staff.userInfo?.surname ?? ''}`.trim() + 
      (staff.stripeActivated ? '' : ` (${t`Stripe not activated`})`),
    value: staff._id,
  }))

  // Show payment fields when immediate consultation is enabled AND payment is required
  const showPaymentFields = isEnabled && !hasNoWaitingRooms && requiresPayment

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        {t`Immediate Consultation`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwitchField name="immediateConsultationConfig.enabled" disabled={readOnly || hasNoWaitingRooms} />
        <Typography>{t`Enable immediate consultations`}</Typography>
      </Box>

      {hasNoWaitingRooms ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t`You need to create a waiting room first, or be an admin of a network with waiting rooms, to enable immediate consultations.`}
        </Alert>
      ) : (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
          {t`Allow patients to join a waiting queue for immediate consultation`}
        </Typography>
      )}

      <Collapse in={isEnabled && !hasNoWaitingRooms}>
        <Box sx={{ pl: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AutocompleteField
            name="waitingRoom"
            label={t`Waiting Room`}
            options={waitingRoomOptions}
            loading={loadingWaitingRooms}
            disabled={readOnly}
            fullWidth
          />

          <TextField
            name="immediateConsultationConfig.maxWaitingPatients"
            type="number"
            label={t`Maximum waiting patients`}
            helperText={t`Leave empty for unlimited`}
            disabled={readOnly}
            fullWidth
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwitchField name="immediateConsultationConfig.requirePaymentUpfront" disabled={readOnly} />
            <Typography>{t`Require payment before joining queue`}</Typography>
          </Box>
        </Box>
      </Collapse>

      {/* Payment fields - rendered separately to avoid nested Collapse issues */}
      <Collapse in={showPaymentFields}>
        <Box sx={{ pl: 4, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            name="immediateConsultationConfig.price"
            type="number"
            label={t`Price`}
            helperText={t`Price for immediate consultations`}
            disabled={readOnly}
            InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
            fullWidth
          />

          <AutocompleteField
            name="immediateConsultationConfig.paymentReceiver"
            label={t`Payment Receiver`}
            options={paymentReceiverOptions}
            disabled={readOnly}
            fullWidth
          />

          {paymentReceiverOptions.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t`No staff with Stripe account activated. Please activate Stripe for at least one staff member.`}
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default ImmediateConsultationConfigForm
